/* raelax — behaviour for rx-* sections: countdown, tabs, carousels, gallery,
   bundle picker, delivery estimate, sticky add-to-cart, dialogs.
   Re-initialises on theme editor section reloads. */
(function () {
  'use strict';

  const pad = (n) => String(n).padStart(2, '0');

  /* ---------- Countdown (counts to a real end date set in the editor) ---------- */
  function initCountdown(root) {
    root.querySelectorAll('[data-rx-countdown]').forEach((el) => {
      if (el._rxTimer) clearInterval(el._rxTimer);
      const raw = (el.dataset.end || '').trim();
      // Accepts "YYYY-MM-DD HH:MM" interpreted in the offset given (default IST +05:30)
      const offset = el.dataset.offset || '+05:30';
      const iso = raw.replace(' ', 'T') + (raw.length <= 16 ? ':00' : '') + offset;
      const end = Date.parse(iso);
      const d = el.querySelector('[data-d]'), dWrap = el.querySelector('[data-days-wrap]');
      const h = el.querySelector('[data-h]'), m = el.querySelector('[data-m]'), s = el.querySelector('[data-s]');
      const wrap = el.closest('[data-rx-countdown-wrap]') || el;
      if (isNaN(end)) { el.hidden = true; return; }
      const tick = () => {
        let diff = Math.max(0, Math.floor((end - Date.now()) / 1000));
        if (diff <= 0) {
          clearInterval(el._rxTimer);
          if (el.dataset.hideExpired === 'true') wrap.hidden = true;
        }
        const days = Math.floor(diff / 86400); diff -= days * 86400;
        const hours = Math.floor(diff / 3600); diff -= hours * 3600;
        const mins = Math.floor(diff / 60); const secs = diff - mins * 60;
        if (dWrap) dWrap.hidden = days < 1;
        if (d) d.textContent = pad(days);
        if (h) h.textContent = pad(hours); if (m) m.textContent = pad(mins); if (s) s.textContent = pad(secs);
      };
      tick();
      el._rxTimer = setInterval(tick, 1000);
    });
  }

  /* ---------- Tabs: [data-rx-tabs] > [data-rx-tab="n"] toggles [data-rx-panel="n"] ---------- */
  function activateTab(group, idx) {
    group.querySelectorAll('[data-rx-tab]').forEach((b) => {
      const on = b.dataset.rxTab === String(idx);
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    group.querySelectorAll('[data-rx-panel]').forEach((p) => {
      if (p.closest('[data-rx-tabs]') !== group) return;
      p.hidden = p.dataset.rxPanel !== String(idx);
    });
  }
  function initTabs(root) {
    root.querySelectorAll('[data-rx-tabs]').forEach((group) => {
      if (group._rxTabs) return; group._rxTabs = true;
      group.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-rx-tab]');
        if (!btn || btn.closest('[data-rx-tabs]') !== group) return;
        activateTab(group, btn.dataset.rxTab);
      });
    });
  }

  /* ---------- Carousels: [data-rx-carousel] with [data-rx-track], [data-rx-prev], [data-rx-next] ---------- */
  function initCarousels(root) {
    root.querySelectorAll('[data-rx-carousel]').forEach((c) => {
      if (c._rxCar) return; c._rxCar = true;
      const track = c.querySelector('[data-rx-track]');
      if (!track) return;
      const step = () => {
        const item = track.firstElementChild;
        if (!item) return track.clientWidth;
        const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
        return c.dataset.rxStep === 'page' ? track.clientWidth : item.getBoundingClientRect().width + gap;
      };
      const update = () => {
        const prev = c.querySelector('[data-rx-prev]'), next = c.querySelector('[data-rx-next]');
        const max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
      };
      c.addEventListener('click', (e) => {
        if (e.target.closest('[data-rx-prev]')) track.scrollBy({ left: -step(), behavior: 'smooth' });
        if (e.target.closest('[data-rx-next]')) track.scrollBy({ left: step(), behavior: 'smooth' });
      });
      track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ---------- Product gallery ---------- */
  function initGallery(root) {
    root.querySelectorAll('[data-rx-gallery]').forEach((g) => {
      if (g._rxGal) return; g._rxGal = true;
      const track = g.querySelector('[data-rx-gallery-track]');
      const thumbs = Array.from(g.querySelectorAll('[data-rx-thumb]'));
      if (!track) return;
      const count = track.children.length;
      const current = () => Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      const go = (i) => {
        const idx = (i + count) % count;
        track.scrollTo({ left: idx * track.clientWidth, behavior: 'smooth' });
      };
      const sync = () => {
        const i = current();
        thumbs.forEach((t, n) => t.classList.toggle('is-active', n === i));
        const active = thumbs[i];
        if (active) {
          const strip = active.parentElement;
          const left = active.offsetLeft - strip.clientWidth / 2 + active.clientWidth / 2;
          strip.scrollTo({ left, behavior: 'smooth' });
        }
      };
      g.addEventListener('click', (e) => {
        if (e.target.closest('[data-rx-gal-prev]')) go(current() - 1);
        if (e.target.closest('[data-rx-gal-next]')) go(current() + 1);
        const t = e.target.closest('[data-rx-thumb]');
        if (t) go(parseInt(t.dataset.rxThumb, 10));
      });
      let raf;
      track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(sync); }, { passive: true });
      // Jump to a variant's image when a bundle with an image is picked
      g.addEventListener('rx:media', (e) => {
        const idx = thumbs.findIndex((t) => t.dataset.mediaId === String(e.detail));
        if (idx > -1) go(idx);
      });
    });
  }

  /* ---------- Bundle picker → hidden variant id + quantity + ATC price ---------- */
  function initBundles(root) {
    root.querySelectorAll('[data-rx-buybox]').forEach((box) => {
      if (box._rxBuy) return; box._rxBuy = true;
      const form = box.querySelector('form[action*="/cart/add"]');
      const idInput = form && form.querySelector('[name="id"]');
      const qtyInput = form && form.querySelector('[name="quantity"]');
      const priceEls = document.querySelectorAll('[data-rx-atc-price]');
      const apply = (input) => {
        if (!input) return;
        box.querySelectorAll('.rx-bundle').forEach((b) => b.classList.toggle('is-selected', b.contains(input)));
        if (idInput && input.value) idInput.value = input.value;
        if (qtyInput) qtyInput.value = input.dataset.qty || 1;
        priceEls.forEach((p) => { p.textContent = input.dataset.price || ''; });
        const btn = box.querySelector('[data-rx-atc]');
        if (btn) {
          const soldOut = input.dataset.available === 'false';
          btn.setAttribute('aria-disabled', soldOut ? 'true' : 'false');
          const lbl = btn.querySelector('[data-rx-atc-label]');
          if (lbl) lbl.textContent = soldOut ? (btn.dataset.soldOut || 'Sold out') : (btn.dataset.label || 'Add to cart');
        }
        if (input.dataset.mediaId) {
          const gal = document.querySelector('[data-rx-gallery]');
          if (gal) gal.dispatchEvent(new CustomEvent('rx:media', { detail: input.dataset.mediaId }));
        }
        if (input.value && box.dataset.updateUrl === 'true' && window.history.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', input.value);
          window.history.replaceState({}, '', url.toString());
        }
      };
      box.addEventListener('change', (e) => { if (e.target.matches('[data-rx-bundle-input]')) apply(e.target); });
      apply(box.querySelector('[data-rx-bundle-input]:checked'));
    });
  }

  /* ---------- Delivery estimate: "Arrives by Oct 07 - Oct 12" ---------- */
  function initDelivery(root) {
    root.querySelectorAll('[data-rx-delivery]').forEach((el) => {
      const min = parseInt(el.dataset.min || '3', 10), max = parseInt(el.dataset.max || '7', 10);
      const skipSun = el.dataset.skipSunday === 'true';
      const add = (days) => {
        const d = new Date();
        let added = 0;
        while (added < days) { d.setDate(d.getDate() + 1); if (!(skipSun && d.getDay() === 0)) added++; }
        return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
      };
      const out = el.querySelector('[data-rx-delivery-range]');
      if (out) out.textContent = add(min) + ' - ' + add(max);
    });
  }

  /* ---------- Sticky add to cart ---------- */
  function initSticky(root) {
    root.querySelectorAll('[data-rx-sticky]').forEach((bar) => {
      if (bar._rxSticky) return; bar._rxSticky = true;
      document.body.classList.add('rx-has-sticky');
      const target = document.querySelector('[data-rx-atc]');
      const toggle = (show) => bar.classList.toggle('is-visible', show);
      if (target && 'IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => {
          toggle(!entry.isIntersecting && entry.boundingClientRect.top < 0);
        }).observe(target);
      } else {
        window.addEventListener('scroll', () => toggle(window.scrollY > 900), { passive: true });
      }
      bar.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-rx-sticky-atc]');
        if (!btn) return;
        e.preventDefault();
        if (target && target.getAttribute('aria-disabled') !== 'true') target.click();
        else if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });
  }

  /* ---------- Dialogs ---------- */
  function initDialogs(root) {
    root.querySelectorAll('[data-rx-open]').forEach((btn) => {
      if (btn._rxDlg) return; btn._rxDlg = true;
      btn.addEventListener('click', () => {
        const d = document.getElementById(btn.dataset.rxOpen);
        if (d && typeof d.showModal === 'function') d.showModal();
      });
    });
    root.querySelectorAll('dialog.rx-dialog').forEach((d) => {
      if (d._rxDlg) return; d._rxDlg = true;
      d.addEventListener('click', (e) => { if (e.target === d || e.target.closest('[data-rx-close]')) d.close(); });
    });
  }

  function init(root) {
    root = root || document;
    initCountdown(root); initTabs(root); initCarousels(root); initGallery(root);
    initBundles(root); initDelivery(root); initSticky(root); initDialogs(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);

  /* Theme editor support */
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const panel = e.target.closest('[data-rx-panel]') || e.target.querySelector('[data-rx-panel]');
    const tab = e.target.closest('[data-rx-tab]');
    const el = panel || tab;
    if (!el) return;
    const group = el.closest('[data-rx-tabs]');
    if (group) activateTab(group, (panel || tab).dataset.rxPanel || (panel || tab).dataset.rxTab);
  });
})();
